import { DEV } from 'cc/env';
import {
    _decorator,
    Component,
    EventTouch,
    Node,
    v3,
    Vec3,
    Rect,
    find,
    UITransform,
    tween,
    Tween,
    Size,
    size,
    Enum,
    CCFloat,
    Widget,
    EventMouse,
    Event,
    ScrollView,
    Vec2,
    v2,
} from 'cc';
import { EasingType, toTweenEasing } from '../../Utils/EasingType';
import BlockDragableEvent from './BlockDragableEvent';

const { ccclass, property } = _decorator;

let canvasTransform: UITransform = null;
let canvasWidget: Widget = null;
const tweenTag = 90;
const MOVE_THRESHOLD = 5;
const tempVec3 = new Vec3();
const tempVec2 = new Vec2();

enum DragableNodeEvent {
    Move = 'Move',
    AdjustToRange = 'AdjustToRange',
}

@ccclass
export default class DragableNode extends Component {
    public static readonly EVENT_TYPE = DragableNodeEvent;

    @property({
        displayName: DEV && '限定水平拖拉',
        tooltip: DEV && '避免移動Y軸',
    })
    protected readonly lockY: boolean = false;

    @property({
        displayName: DEV && '限定垂直拖拉',
        tooltip: DEV && '避免移動X軸',
    })
    protected readonly lockX: boolean = false;

    @property({
        displayName: DEV && '防止超出範圍',
        tooltip: DEV && '避免此節點與其子節點的ContentSize超出範圍',
    })
    private readonly preventOutofRange: boolean = false;

    @property({
        displayName: DEV && '判斷超出範圍時是否檢查自身子節點',
        visible(this: DragableNode) {
            return this.preventOutofRange;
        },
    })
    private readonly checkChildrenRange: boolean = true;

    @property({
        type: [Node],
        displayName: DEV && '不想被算入檢查的子節點',
        visible(this: DragableNode) {
            return this.preventOutofRange && this.checkChildrenRange;
        },
    })
    private readonly nonCheckableChildren: Node[] = [];

    @property({
        displayName: DEV && '判斷超出畫面',
        tooltip: DEV && '檢查超出Design Resolution範圍',
        visible(this: DragableNode) {
            return this.preventOutofRange;
        },
    })
    private readonly checkScreen: boolean = true;

    @property({
        displayName: DEV && '範圍目標節點',
        visible(this: DragableNode) {
            return !this.checkScreen && this.preventOutofRange;
        },
        type: Node,
    })
    private readonly targetNode: Node = null;

    @property({
        displayName: DEV && '是否考慮範圍目標節點的子節點',
        visible(this: DragableNode) {
            return !this.checkScreen && this.preventOutofRange;
        },
    })
    private readonly considerTargetChildren: boolean = true;

    @property({
        displayName: DEV && '內距',
        visible(this: DragableNode) {
            return this.preventOutofRange;
        },
    })
    private readonly padding: Size = size();

    @property({
        displayName: DEV && '強行限制',
        tooltip: DEV && '拖曳時不能離開限制範圍',
        visible(this: DragableNode) {
            return this.preventOutofRange;
        },
    })
    private readonly hardRestrict: boolean = false;

    @property({
        displayName: DEV && '緩動時長',
        tooltip: DEV && '恢復到範圍內的時長, 單位: 秒',
        visible(this: DragableNode) {
            return this.preventOutofRange && !this.hardRestrict;
        },
        type: CCFloat,
    })
    private readonly recoveryPositionDuration: number = 0.2;

    @property({
        displayName: DEV && '緩動方式',
        visible(this: DragableNode) {
            return this.preventOutofRange && !this.hardRestrict;
        },
        type: Enum(EasingType),
    })
    private readonly recoveryPositionEasing: EasingType = EasingType.linear;

    @property({
        displayName: DEV && '限制使用右鍵拖拉',
        tooltip: DEV && '啟用時, 只能透過右鍵拖拉',
    })
    private readonly rightClickToDrag: boolean = false;

    @property({
        displayName: DEV && '取消子節點事件',
        tooltip: DEV && '如果這個屬性被設為 true，則滑動行為將會取消子節點上註冊的觸摸、點擊事件，默認設置為 true。',
    })
    private readonly cancelInnerEvents: boolean = true;

    private _moveOffset: Vec3 = null;
    private _originPos: Vec3 = null;
    private _hasMoved: boolean = false;

    public get isMoving(): boolean {
        return this._hasMoved;
    }

    public get currentDragDistance() {
        return this._originPos && this._originPos.clone().subtract(this.node.position).length();
    }

    // 回到範圍內
    public recoveryToRange(immediately: boolean = false): void {
        const result = this.checkOutOfRange();
        if (result.outOfRange) {
            this._adjustToRange(result.range, result.node, immediately);
        }
    }

    protected onLoad(): void {
        if (this.rightClickToDrag) {
            this.node.on(
                Node.EventType.MOUSE_DOWN,
                (event: EventMouse, captureListeners: Node[]) => {
                    // 右鍵判斷
                    if (event.getButton() === EventMouse.BUTTON_RIGHT) {
                        this.onTouchStart(event, captureListeners);
                    }
                },
                this,
                true,
            ); // 點下去時
            this.node.on(
                Node.EventType.MOUSE_UP,
                (event: EventMouse) => {
                    // 右鍵判斷
                    if (event.getButton() === EventMouse.BUTTON_RIGHT) {
                        this.onTouchEnd(event);
                    }
                },
                this,
                true,
            ); // 放開(範圍內)
            this.node.on(
                Node.EventType.MOUSE_MOVE,
                (event: EventMouse) => {
                    if (event.getButton() === EventMouse.BUTTON_RIGHT) {
                        this.onTouchMove(event);
                    }
                },
                this,
                true,
            ); // 移動時
        } else {
            this.node.on(Node.EventType.TOUCH_START, this.onTouchStart, this, true); // 點下去時
            this.node.on(Node.EventType.TOUCH_END, this.onTouchEnd, this, true); // 放開(範圍內)
            this.node.on(Node.EventType.TOUCH_MOVE, this.onTouchMove, this, true); // 移動時
        }
        this.node.on(Node.EventType.TOUCH_CANCEL, this.onTouchEnd, this, true); // 放開(範圍外)
    }

    protected checkOutOfRange(): { range: Rect; node: Rect; outOfRange: boolean } {
        if (!this.preventOutofRange) {
            return { range: null, node: null, outOfRange: false };
        }
        let range: Rect = null;
        // 避免超出顯示畫面的話, 以Canvas範圍為準
        if (this.checkScreen) {
            if (!canvasTransform?.isValid) {
                canvasTransform = find('Canvas').transform;
            }
            if (!canvasWidget?.isValid) {
                canvasWidget = find('Canvas').getComponent(Widget);
            }
            // 因為 Widget 收到 design-resolution-changed 後才會更新，直接抓取 transform 不會抓取到正確的寬高。所以要主動更新
            canvasWidget?.updateAlignment();
            range = canvasTransform.getBoundingBox();
        } else {
            range = this.considerTargetChildren
                ? this.targetNode.transform.getVisibleWorldBoundingBoxByRectSet()
                : this.targetNode.transform.getWorldBoundingBox();
        }
        const boundingBox = this.checkChildrenRange
            ? this.node.transform.getVisibleWorldBoundingBoxByRectSet(this.nonCheckableChildren)
            : this.node.transform.getWorldBoundingBox();
        return {
            range,
            node: boundingBox,
            outOfRange: !range.containsRect(boundingBox),
        };
    }

    protected calculateRangeOffset(range: Rect, node: Rect): Vec2 {
        const exceedMaxX = node.xMax > range.xMax;
        const exceedMaxY = node.yMax > range.yMax;
        const exceedMinX = node.xMin < range.xMin;
        const exceedMinY = node.yMin < range.yMin;
        // 如果左右 或 上下 同時超出範圍, 代表其本體超過範圍尺寸, 調整也沒用
        const adjustX = (exceedMaxX || exceedMinX) && !(exceedMaxX && exceedMinX);
        const adjustY = (exceedMaxY || exceedMinY) && !(exceedMaxY && exceedMinY);
        let offsetX = 0;
        let offsetY = 0;
        if (adjustX) {
            offsetX = exceedMaxX ? range.xMax - node.xMax - this.padding.width : range.xMin - node.xMin + this.padding.width;
        }
        if (adjustY) {
            offsetY = exceedMaxY ? range.yMax - node.yMax - this.padding.height : range.yMin - node.yMin + this.padding.height;
        }
        return v2(offsetX, offsetY);
    }

    protected onTouchStart(event: EventTouch | EventMouse, captureListeners: Node[]): void {
        if (!this.enabledInHierarchy) {
            return;
        }

        // retrieve touch location in world space
        let x = 0;
        let y = 0;
        if (event instanceof EventTouch) {
            const location = event.touch.getUILocation();
            ({ x, y } = location);
            event.touch.getLocation(tempVec2);
        } else {
            const location = event.getUILocation();
            ({ x, y } = location);
            event.getLocation(tempVec2);
        }

        if (!this._checkIfShouldHandleEvent(event, captureListeners, tempVec2)) {
            return;
        }

        // lock position
        this.node.getWorldPosition(tempVec3);
        if (this.lockX) {
            ({ x } = tempVec3);
        }
        if (this.lockY) {
            ({ y } = tempVec3);
        }

        this._calcMoveOffset(x, y);
        this._originPos = this.node.position.clone();
        this._stopPropagationIfTargetIsMe(event);
    }

    protected onTouchMove(event: EventTouch | EventMouse): void {
        if (!this.enabledInHierarchy || !this._moveOffset) {
            return;
        }

        // retrieve touch location in world space
        let x = 0;
        let y = 0;
        if (event instanceof EventTouch) {
            const location = event.touch.getUILocation();
            ({ x, y } = location);
        } else {
            const location = event.getUILocation();
            ({ x, y } = location);
        }

        // lock position
        this.node.getWorldPosition(tempVec3);
        if (this.lockX) {
            ({ x } = tempVec3);
        }
        if (this.lockY) {
            ({ y } = tempVec3);
        }

        // 轉換為父節點的座標系統
        const localPos = this.node.parent.transform.convertToNodeSpaceAR(v3(x, y));
        // 將坐標扣掉滑鼠跟節點的差值，避免節點的直接以滑鼠坐標對齊
        localPos.subtract(this._moveOffset);
        this.node.getPosition(tempVec3);
        this.node.setPosition(localPos);
        this.emit(DragableNode.EVENT_TYPE.Move);
        Tween.stopAllByTag(tweenTag, this.node);

        if (this.preventOutofRange && this.hardRestrict) {
            const result = this.checkOutOfRange();
            if (result.outOfRange) {
                this.node.setPosition(tempVec3);
                this._calcMoveOffset(x, y);
            }
        }

        // cancel inner events if move distance is over threshold
        if (this.cancelInnerEvents && !this._hasMoved && event.target !== this.node) {
            if (tempVec3.subtract(this._originPos).length() > MOVE_THRESHOLD) {
                // Simulate touch cancel for target node
                if (event instanceof EventTouch) {
                    const cancelEvent = new EventTouch(event.getTouches(), event.bubbles, Node.EventType.TOUCH_CANCEL);
                    cancelEvent.touch = event.touch;
                    cancelEvent.simulate = true;
                    (event.target as Node).dispatchEvent(cancelEvent);
                    this._hasMoved = true;
                } else if (event instanceof EventMouse) {
                    // stop propagation directly for mouse event, because we can't simulate mouse cancel event
                    event.propagationStopped = true;
                }
            }
        }
        this._stopPropagationIfTargetIsMe(event);
    }

    protected onTouchEnd(event: EventTouch | EventMouse) {
        if (!this.enabledInHierarchy || !this._moveOffset || (event instanceof EventTouch && event.simulate)) {
            return;
        }
        if (this.cancelInnerEvents) {
            if (event instanceof EventMouse) {
                event.propagationStopped = true;
            } else {
                // touch event is handled by sending simulate touch cancel event in touch move, so we don't need to stop propagation here
            }
        }
        this.recoveryToRange();
        this._stopPropagationIfTargetIsMe(event);
        this._moveOffset = null;
        this._hasMoved = false;
    }

    private _calcMoveOffset(x: number, y: number) {
        // 紀錄點下去的時候，滑鼠坐標距離節點坐標多遠。需要考慮 scale，因此差值應該除以 scale。
        this._moveOffset = this.node.transform.convertToNodeSpaceAR(v3(x, y));
        this._moveOffset = this._moveOffset.clone().multiply3f(this.node.scale.x, this.node.scale.y, 1);
    }

    private _stopPropagationIfTargetIsMe(event: EventTouch | EventMouse) {
        if (event.eventPhase === Event.AT_TARGET && event.target === this.node) {
            event.propagationStopped = true;
        }
    }

    private _adjustToRange(range: Rect, node: Rect, immediately: boolean = false) {
        const { x: offsetX, y: offsetY } = this.calculateRangeOffset(range, node);

        if (offsetX !== 0 || offsetY !== 0) {
            this.emit(DragableNode.EVENT_TYPE.AdjustToRange);
            tween(this.node)
                .to(
                    immediately ? 0 : this.recoveryPositionDuration,
                    { position: this.node.position.clone().add3f(offsetX, offsetY, 0) },
                    { easing: toTweenEasing(this.recoveryPositionEasing) },
                )
                .tag(tweenTag)
                .start();
        }
    }

    /**
     * check if position is out of range or any other dragable node is under the click point or scroll view exist
     * @param event event
     * @param captureListeners current capture listeners of the event
     * @param clickPoint revised click point after lock checking
     * @returns should handle event or not
     */
    private _checkIfShouldHandleEvent(event: Event, captureListeners: Node[], clickPoint: Vec2): boolean {
        // if event target is node itself, always handle event, since click point must be inside the node
        if (event.target === this.node && event.eventPhase === Event.AT_TARGET) {
            return true;
        }
        // if any dragable node is under the click point or scroll view exist, don't handle event
        for (let i = 0; i < captureListeners.length; i++) {
            const listener = captureListeners[i];
            if (listener === this.node) {
                if (this._hasDragableNodeOrScrollViewOrBlockDragable(event.target, clickPoint)) {
                    return false;
                }
                return this.node.transform.hitTest(clickPoint);
            }

            if (this._hasDragableNodeOrScrollViewOrBlockDragable(listener, clickPoint)) {
                return false;
            }
        }
        return true;
    }

    private _hasDragableNodeOrScrollViewOrBlockDragable(node: Node, clickPoint: Vec2): boolean {
        const dragableNode = node.getComponent(DragableNode);
        if (dragableNode && dragableNode.enabledInHierarchy && node.transform.hitTest(clickPoint)) {
            return true;
        }
        const scrollView = node.getComponent(ScrollView);
        if (scrollView && scrollView.enabledInHierarchy) {
            return true;
        }
        const blockDragableEvent = node.getComponent(BlockDragableEvent);
        if (blockDragableEvent && blockDragableEvent.enabledInHierarchy && node.transform.hitTest(clickPoint)) {
            return true;
        }

        return false;
    }
}
